<template>
  <div>
    <!-- Headless uploader: reuses the pick → crop → upload flow, hidden from view.
         The crop modal teleports to <body>, so hiding this wrapper is safe. -->
    <div class="hidden">
      <image-upload
        ref="uploaderRef"
        origin="gallery"
        :uploadFunction="onUploaded"
      />
    </div>

    <draggable
      v-model="images"
      item-key="src"
      :animation="180"
      filter=".gallery-caption"
      :prevent-on-filter="false"
      class="grid grid-cols-3 gap-2"
    >
      <template #item="{ element, index }">
        <div class="relative aspect-square rounded-xl overflow-hidden bg-surface-100 cursor-grab active:cursor-grabbing">
          <img :src="element.src" alt="gallery image" class="w-full h-full object-cover" />

          <!-- Remove -->
          <button
            type="button"
            class="absolute top-1.5 left-1.5 z-10 w-[22px] h-[22px] rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center"
            @click="removeAt(index)"
          >
            <i class="pi pi-trash text-white" style="font-size: 0.7rem" />
          </button>

          <!-- Caption -->
          <div class="absolute inset-x-0 bottom-0 pt-3.5 px-1.5 pb-1.5 bg-gradient-to-t from-black/60 to-transparent">
            <input
              class="gallery-caption w-full border-none outline-none bg-transparent text-white text-[10.5px] font-medium text-right cursor-text p-0 placeholder:text-white/70"
              style="direction: rtl; text-shadow: 0 1px 2px rgba(0,0,0,0.4)"
              :value="element.caption || ''"
              placeholder="+ כיתוב"
              @input="setCaption(index, ($event.target as HTMLInputElement).value)"
            />
          </div>
        </div>
      </template>

      <template #footer>
        <div
          v-if="images.length < max"
          class="aspect-square rounded-xl border-[1.5px] border-dashed border-surface-300 bg-surface-50 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
          @click="triggerAdd"
        >
          <div class="w-[30px] h-[30px] rounded-full bg-primary-100 flex items-center justify-center">
            <i class="pi pi-plus text-primary-700 text-base" />
          </div>
          <span class="text-[10.5px] font-medium text-surface-600">הוספה</span>
        </div>
      </template>
    </draggable>

    <div class="flex items-center gap-1.5 mt-2.5 text-[10.5px] text-surface-400">
      <span class="grid grid-cols-2 gap-[2px]">
        <i v-for="n in 6" :key="n" class="w-[2.5px] h-[2.5px] rounded-full bg-surface-400 block" />
      </span>
      <span class="flex-1">גררו לסידור מחדש · הקלידו כיתוב על כל תמונה · JPG / PNG עד 5 מ״ב</span>
      <span class="font-semibold text-surface-500">{{ images.length }} / {{ max }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import draggable from 'vuedraggable';
import { IGalleryImage } from '@torim-app/types';
import ImageUpload from '@app/components/admin/components/ImageUpload.vue';

const props = withDefaults(
  defineProps<{
    modelValue: IGalleryImage[];
    max?: number;
  }>(),
  { max: 12 }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: IGalleryImage[]): void;
}>();

const uploaderRef = ref<InstanceType<typeof ImageUpload>>();

const images = computed<IGalleryImage[]>({
  get: () => props.modelValue ?? [],
  set: (value) => emit('update:modelValue', value),
});

const triggerAdd = () => {
  if (images.value.length >= props.max) return;
  uploaderRef.value?.pickImage();
};

const onUploaded = (url: string) => {
  if (!url || images.value.length >= props.max) return;
  emit('update:modelValue', [...images.value, { src: url, show: true }]);
};

const removeAt = (index: number) => {
  emit('update:modelValue', images.value.filter((_, i) => i !== index));
};

const setCaption = (index: number, caption: string) => {
  emit('update:modelValue', images.value.map((image, i) => (i === index ? { ...image, caption } : image)));
};
</script>
